import { InfoCircleTwoTone } from "@ant-design/icons";
import { DEFAULT_AUTHN_LOOKBACK_DAYS } from "@p0security/shared/assessment/constants";
import {
  Identity,
  IdentityType,
} from "@p0security/shared/types/assessment/data/index";
import { Typography } from "@p0security/ui";
import { GraphTooltip } from "components/GraphTooltip";
import { isAfter, subDays } from "date-fns";
import { ReactNode } from "react";

import { Timestamp } from "../../../Timestamp";

const { Text } = Typography;

export const authnHelp: Record<IdentityType, ReactNode> = {
  "aws-iam-role":
    "AWS IAM roles inherit authentication from their trust policies.",
  "aws-permission-set-role":
    "Permission-set roles are only used for federated access.",
  "entra-app-registration":
    "Authentication information is not yet available for Entra app registrations.",
  domain: "Domains do not have authentication credentials.",
  federated: "Authentication information is not yet available.",
  group: "Groups do not have authentication credentials.",
  "logged-in":
    "This is an arbitrary user, and does not refer to a user with a unique set of authentication credentials.",
  public: "Public Internet users are not authenticated.",
  "service-account":
    "Authentication information is only available for user-managed service accounts.",
  "service-principal":
    "Authentication information is only available for user-managed service principals.",
  unknown: "Authentication time is unknown.",
  user: "Authentication information is unknown for this user. Install the directory integration corresponding to this user account to see its authentication details.",
};

export const LastAuthenticated: React.FC<{
  identity: Identity;
  last?: number;
  createdAt?: number;
  lookback?: number;
}> = ({ last, identity: identity, lookback, createdAt }) => {
  const lookbackDays = lookback ?? DEFAULT_AUTHN_LOOKBACK_DAYS;
  return (
    <>
      {last === undefined || isNaN(last) ? (
        <Text type="secondary">
          <GraphTooltip title={authnHelp[identity?.type ?? "unknown"]}>
            <InfoCircleTwoTone />
          </GraphTooltip>
          &nbsp;(Unknown)
        </Text>
      ) : last === 0 ? (
        <Text type="secondary">
          {`Unused ${
            // If the identity/credential was created fewer than lookback days ago,
            // it has instead been unused since creation.
            createdAt && isAfter(createdAt, subDays(Date.now(), lookbackDays))
              ? `since creation`
              : `for ${lookbackDays} days`
          }`}
        </Text>
      ) : (
        <Timestamp at={last} />
      )}
    </>
  );
};
